/**
 * LinkedIn Sales Navigator Filter Panel Configuration
 * Defines all filter categories and their filters based on LinkedIn's structure
 */
import { Business as CompanyIcon, Language as LanguageIcon, LocationOn as LocationIcon, People as PeopleIcon, Person as PersonIcon, School as SchoolIcon, Star as StarIcon, Timeline as TimelineIcon, Visibility as SpotlightIcon, Work as WorkIcon, } from "@mui/icons-material";
// Company filters
export const COMPANY_FILTERS = [
    {
        id: "company",
        label: "Company",
        icon: CompanyIcon,
        filterType: "textSearch",
    },
    // TODO: Re-enable when current/past company separation is supported on Recruiter.
    // {
    //   id: "currentCompany",
    //   label: "Current company",
    //   icon: CompanyIcon,
    //   filterType: "textSearch",
    // },
    // {
    //   id: "pastCompany",
    //   label: "Past company",
    //   icon: CompanyIcon,
    //   filterType: "textSearch",
    // },
    { id: "companyHeadcount", label: "Company headcount", icon: PeopleIcon },
    { id: "companyType", label: "Company type", icon: CompanyIcon },
    // TODO: Re-enable when companyHeadquarters is supported on Recruiter.
    // This filter only works on Sales Navigator — uncomment to restore.
    // {
    //   id: "companyHeadquarters",
    //   label: "Company headquarters location",
    //   icon: LocationIcon,
    //   filterType: "textSearch",
    // },
];
// Role filters
export const ROLE_FILTERS = [
    { id: "function", label: "Function", icon: WorkIcon },
    { id: "jobTitle", label: "Job title", icon: WorkIcon, filterType: "textSearch" },
    // TODO: Re-enable when current/past job title separation is supported on Recruiter.
    // { id: "currentJobTitle", label: "Current job title", icon: WorkIcon, filterType: "textSearch" },
    // { id: "pastJobTitle", label: "Past job title", icon: WorkHistoryIcon, filterType: "textSearch" },
    { id: "seniorityLevel", label: "Seniority level", icon: StarIcon },
    { id: "yearsInCurrentCompany", label: "Years in current company", icon: TimelineIcon },
    { id: "yearsInCurrentPosition", label: "Years in current position", icon: TimelineIcon },
];
// Personal filters
export const PERSONAL_FILTERS = [
    { id: "geography", label: "Geography", icon: LocationIcon, filterType: "textSearch" },
    {
        id: "postalCode",
        label: "Postal code / Zip code",
        icon: LocationIcon,
        filterType: "postalCode",
    },
    {
        id: "industry",
        label: "Industry",
        icon: CompanyIcon,
        filterType: "textSearch",
    },
    { id: "firstName", label: "First name", icon: PersonIcon },
    { id: "lastName", label: "Last name", icon: PersonIcon },
    { id: "profileLanguage", label: "Profile language", icon: LanguageIcon },
    { id: "yearsOfExperience", label: "Years of experience", icon: TimelineIcon },
    { id: "school", label: "School", icon: SchoolIcon, filterType: "textSearch" },
];
// Spotlight filters (Recruiter-only)
export const SPOTLIGHT_FILTERS = [
    {
        id: "openToWork",
        label: "Ready For Change",
        icon: SpotlightIcon,
    },
];
// Recent Updates filters
// TODO: Re-enable when changedJobs / postedOnLinkedIn are supported on Recruiter.
// These filters only work on Sales Navigator — uncomment to restore the section.
// export const RECENT_UPDATES_FILTERS: FilterConfig[] = [
//   {
//     id: "changedJobs",
//     label: "Changed jobs",
//     icon: NotificationsIcon,
//   },
//   {
//     id: "postedOnLinkedIn",
//     label: "Posted on LinkedIn",
//     icon: HistoryIcon,
//   },
// ];
export const RECENT_UPDATES_FILTERS = [];
// All filter sections (removed Buyer Intent, Best Path In, and Workflow)
export const FILTER_SECTIONS = [
    { id: "company", title: "Company", filters: COMPANY_FILTERS, defaultExpanded: true },
    { id: "role", title: "Role", filters: ROLE_FILTERS, defaultExpanded: true },
    { id: "personal", title: "Personal", filters: PERSONAL_FILTERS, defaultExpanded: true },
    // TODO: Uncomment when changedJobs / postedOnLinkedIn are supported on Recruiter
    // { id: "recentUpdates", title: "Recent Updates", filters: RECENT_UPDATES_FILTERS, defaultExpanded: false },
    { id: "spotlights", title: "Spotlights", filters: SPOTLIGHT_FILTERS, defaultExpanded: true },
];
// Filters that use text search with include/exclude
export const TEXT_SEARCH_FILTER_IDS = [
    "company",
    // TODO: Re-enable when current/past separation is supported on Recruiter.
    // "currentCompany",
    // "pastCompany",
    "companyHeadquarters",
    "jobTitle",
    // "currentJobTitle",
    // "pastJobTitle",
    "geography",
    "industry",
    "school",
];
// Filters that use chip select (predefined options)
export const CHIP_SELECT_FILTER_IDS = [
    "companyHeadcount",
    "companyType",
    "function",
    "seniorityLevel",
    "yearsInCurrentCompany",
    "yearsInCurrentPosition",
    "profileLanguage",
    "yearsOfExperience",
];
// Filters that use simple text input (single value)
export const SIMPLE_TEXT_FILTER_IDS = ["firstName", "lastName"];
// Filters that use boolean toggle
export const BOOLEAN_FILTER_IDS = ["changedJobs", "postedOnLinkedIn", "openToWork"];
// Chip select filter options configuration
export const CHIP_SELECT_OPTIONS = {
    companyHeadcount: {
        options: [
            "Self-employed",
            "1-10",
            "11-50",
            "51-200",
            "201-500",
            "501-1,000",
            "1,001-5,000",
            "5,001-10,000",
            "10,001+",
        ],
        mode: "simple",
    },
    companyType: {
        options: [
            "Public Company",
            "Privately Held",
            "Non Profit",
            "Educational Institution",
            "Partnership",
            "Self Employed",
            "Self Owned",
            "Government Agency",
        ],
        mode: "simple",
    },
    function: {
        options: [
            "Accounting",
            "Administrative",
            "Arts and Design",
            "Business Development",
            "Community and Social Services",
            "Consulting",
            "Customer Success and Support",
            "Education",
            "Engineering",
            "Entrepreneurship",
            "Finance",
            "Healthcare Services",
            "Human Resources",
            "Information Technology",
            "Legal",
            "Marketing",
            "Media and Communication",
            "Military and Protective Services",
            "Operations",
            "Product Management",
            "Program and Project Management",
            "Purchasing",
            "Quality Assurance",
            "Real Estate",
            "Research",
            "Sales",
        ],
        mode: "simple",
        searchable: true,
        layout: "list",
    },
    seniorityLevel: {
        options: [
            "Entry Level",
            "Director",
            "In Training",
            "Manager",
            "Owner / Partner",
            "CXO",
            "Vice President",
            "Senior",
        ],
        mode: "simple",
    },
    yearsInCurrentCompany: {
        options: ["Less than 1 year", "1 to 2 years", "3 to 5 years", "6 to 10 years", "More than 10 years"],
        mode: "simple",
        singleSelect: true,
    },
    yearsInCurrentPosition: {
        options: ["Less than 1 year", "1 to 2 years", "3 to 5 years", "6 to 10 years", "More than 10 years"],
        mode: "simple",
        singleSelect: true,
    },
    profileLanguage: {
        options: [
            "Arabic",
            "English",
            "Spanish",
            "Portuguese",
            "Chinese",
            "French",
            "Italian",
            "Russian",
            "German",
            "Dutch",
            "Turkish",
            "Tagalog",
            "Polish",
            "Korean",
            "Japanese",
            "Malay",
            "Norwegian",
            "Danish",
            "Romanian",
            "Swedish",
            "Bahasa Indonesia",
            "Czech",
        ],
        mode: "simple",
    },
    yearsOfExperience: {
        options: ["Less than 1 year", "1 to 2 years", "3 to 5 years", "6 to 10 years", "More than 10 years"],
        mode: "simple",
        singleSelect: true,
    },
};
const emptyTextSearchValue = { included: [], excluded: [] };
const emptyChipSelectValue = { included: [], excluded: [] };
export const createEmptyLinkedInFilters = () => ({
    // Keywords
    keywords: "",
    // Company - text search filters
    company: Object.assign({}, emptyTextSearchValue),
    companyHeadquarters: Object.assign({}, emptyTextSearchValue),
    // Company - chip select filters
    companyHeadcount: Object.assign({}, emptyChipSelectValue),
    companyType: Object.assign({}, emptyChipSelectValue),
    // Role - text search filters
    jobTitle: Object.assign({}, emptyTextSearchValue),
    // Role - chip select filters
    function: Object.assign({}, emptyChipSelectValue),
    // Role - chip select filters
    seniorityLevel: Object.assign({}, emptyChipSelectValue),
    yearsInCurrentCompany: Object.assign({}, emptyChipSelectValue),
    yearsInCurrentPosition: Object.assign({}, emptyChipSelectValue),
    // Personal - text search filters
    geography: Object.assign({}, emptyTextSearchValue),
    industry: Object.assign({}, emptyTextSearchValue),
    school: Object.assign({}, emptyTextSearchValue),
    // Personal - chip select filters
    profileLanguage: Object.assign({}, emptyChipSelectValue),
    yearsOfExperience: Object.assign({}, emptyChipSelectValue),
    // Personal - other filters
    firstName: "",
    lastName: "",
    // Recent Updates
    changedJobs: null,
    postedOnLinkedIn: null,
    // Spotlights
    openToWork: null,
    // Recruiter-only location
    postalCode: null,
});
/**
 * Check if an include/exclude filter has active values
 */
const isIncludeExcludeFilterActive = (value) => {
    return value.included.length > 0 || value.excluded.length > 0;
};
/**
 * Check if a filter has active values
 */
export const isFilterActive = (filters, filterId) => {
    if (filterId === "postalCode") {
        return filters.postalCode !== null && filters.postalCode.location.trim() !== "";
    }
    const value = filters[filterId];
    // Check if it's a text search filter
    if (TEXT_SEARCH_FILTER_IDS.includes(filterId)) {
        return isIncludeExcludeFilterActive(value);
    }
    // Check if it's a chip select filter
    if (CHIP_SELECT_FILTER_IDS.includes(filterId)) {
        return isIncludeExcludeFilterActive(value);
    }
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    if (typeof value === "string") {
        return value.length > 0;
    }
    if (typeof value === "boolean") {
        return value !== null;
    }
    return value !== null;
};
/**
 * Count active filters in a section
 */
export const countSectionFilters = (filters, sectionId) => {
    const section = FILTER_SECTIONS.find((s) => s.id === sectionId);
    if (!section)
        return 0;
    return section.filters.filter((f) => isFilterActive(filters, f.id)).length;
};
/**
 * Count total active filters
 */
export const countActiveFilters = (filters) => {
    return FILTER_SECTIONS.reduce((total, section) => total + countSectionFilters(filters, section.id), 0);
};
